import {
	AlertCircleIcon,
	AlertDiamondIcon,
	Search01Icon,
	Tick02Icon,
	Time04Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, router } from "@inertiajs/react";
import type { VariantProps } from "class-variance-authority";
import { useState } from "react";
import Dashboard from "@/components/dashboard";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { VerificationStatusBadge } from "@/components/verification-status-badge";
import { formatBirthNumber } from "@/lib/formatters";
import { getRelationshipLabel } from "@/lib/labels";
import { cn } from "@/lib/utils";
import type HomeUser from "@/types/serializers/HomeUser";
import type PhysicalPersonsClientLink from "@/types/serializers/PhysicalPersonsClientLink";
import type PhysicalPersonsPhysicalPerson from "@/types/serializers/PhysicalPersonsPhysicalPerson";

interface Props {
	user: HomeUser;
	physicalPersons: PhysicalPersonsPhysicalPerson[];
	filters: {
		search?: string;
		verification_status?: string;
	};
}

const verificationStatusFilterItems: {
	value: string | null;
	label: string;
	variant?: VariantProps<typeof badgeVariants>["variant"];
	icon?: typeof Tick02Icon;
}[] = [
	{ value: null, label: "Všechny stavy identifikace" },
	{ value: "verified", label: "Ověřeno", variant: "default", icon: Tick02Icon },
	{
		value: "partially_verified",
		label: "Částečně ověřeno",
		variant: "outline",
	},
	{ value: "not_verified", label: "Neověřeno", variant: "secondary" },
	{
		value: "expiring_soon",
		label: "Expiruje brzy",
		variant: "warning",
		icon: Time04Icon,
	},
	{
		value: "expired",
		label: "Expirováno",
		variant: "destructive",
		icon: AlertDiamondIcon,
	},
];

const MISSING_INFO_LABELS: Record<string, string> = {
	missing_birth_number: "rodné číslo",
	missing_person_address: "adresa bydliště",
	missing_document: "doklad totožnosti",
	missing_birth_info: "údaje o narození",
	missing_citizenship: "státní občanství",
	missing_gender: "pohlaví",
	missing_aml_check: "AML kontrola (sankce/PEP)",
	document_expired: "dokument expiroval",
	document_expiring: "dokument brzy expiruje",
};

const RELATIONSHIP_PRIORITY: Record<
	PhysicalPersonsClientLink["relationship"],
	number
> = {
	ubo: 0,
	statutory_member: 0,
	ubo_and_statutory: 0,
	direct_entrepreneur: 1,
	direct_individual: 2,
};

function sortClientLinks(
	links: PhysicalPersonsClientLink[],
): PhysicalPersonsClientLink[] {
	return [...links].sort((a, b) => {
		const priorityDiff =
			RELATIONSHIP_PRIORITY[a.relationship] -
			RELATIONSHIP_PRIORITY[b.relationship];
		if (priorityDiff) return priorityDiff;
		return a.client_name.localeCompare(b.client_name);
	});
}

function getClientDisplayName(link: PhysicalPersonsClientLink): string {
	if (
		link.relationship === "direct_individual" ||
		link.relationship === "direct_entrepreneur"
	) {
		return getRelationshipLabel(link.relationship);
	}
	return link.client_name;
}

export default function PhysicalPersonsIndex({
	user,
	physicalPersons,
	filters,
}: Props) {
	const [search, setSearch] = useState(filters.search || "");

	const handleSearch = (value: string) => {
		setSearch(value);
		router.get(
			"/physical_persons",
			{
				search: value || undefined,
				verification_status: filters.verification_status || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	const handleVerificationStatusFilter = (value: string | null) => {
		router.get(
			"/physical_persons",
			{
				search: filters.search || undefined,
				verification_status: value || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	return (
		<>
			<Head title="Osoby" />
			<Dashboard user={user}>
				<div className="flex flex-col gap-4 p-4 lg:p-6">
					<div className="flex items-center justify-between">
						<h2 className="font-semibold text-2xl">Osoby</h2>
					</div>

					<div className="flex items-center gap-4">
						<div className="relative flex-1">
							<HugeiconsIcon
								icon={Search01Icon}
								strokeWidth={2}
								className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
							/>
							<Input
								placeholder="Hledat..."
								value={search}
								onChange={(e) => handleSearch(e.target.value)}
								className="pl-8"
							/>
						</div>
						<Select
							value={filters.verification_status}
							onValueChange={handleVerificationStatusFilter}
							items={verificationStatusFilterItems}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{verificationStatusFilterItems.map((item) => (
									<SelectItem key={item.value} value={item.value}>
										{item.variant ? (
											<Badge variant={item.variant} className="gap-1">
												{item.icon && (
													<HugeiconsIcon
														icon={item.icon}
														strokeWidth={2}
														className="size-3"
													/>
												)}
												{item.label}
											</Badge>
										) : (
											item.label
										)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="rounded-xl border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Jméno</TableHead>
									<TableHead>RČ</TableHead>
									<TableHead>Klienti</TableHead>
									<TableHead>Sankce</TableHead>
									<TableHead>PEP</TableHead>
									<TableHead>Identifikace</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{physicalPersons.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={6}
											className="py-8 text-center text-muted-foreground"
										>
											Žádné osoby nenalezeny
										</TableCell>
									</TableRow>
								) : (
									physicalPersons.map((person) => {
										const hasErrors =
											person.missing_info.includes("document_expired");
										return (
											<TableRow
												key={person.id}
												className={cn(
													"cursor-pointer",
													person.is_sanctioned === true &&
														"bg-destructive/10 hover:bg-destructive/15",
												)}
												onClick={() =>
													router.visit(`/physical_persons/${person.id}`)
												}
											>
												<TableCell className="font-medium">
													<div className="flex items-center gap-2">
														{person.full_name}
														{person.has_missing_info && (
															<Tooltip>
																<TooltipTrigger>
																	<HugeiconsIcon
																		icon={AlertCircleIcon}
																		strokeWidth={2}
																		className={`size-4 ${hasErrors ? "text-destructive" : "text-warning"}`}
																	/>
																</TooltipTrigger>
																<TooltipContent>
																	<MissingInfoList
																		missingInfo={person.missing_info}
																	/>
																</TooltipContent>
															</Tooltip>
														)}
													</div>
												</TableCell>
												<TableCell>
													{formatBirthNumber(person.birth_number) || (
														<span className="text-muted-foreground">—</span>
													)}
												</TableCell>
												<TableCell>
													<ClientLinksBadge clientLinks={person.client_links} />
												</TableCell>
												<TableCell>
													{person.is_sanctioned === true ? (
														<Badge variant="destructive">Ano</Badge>
													) : person.is_sanctioned === false ? (
														<Badge variant="secondary">Ne</Badge>
													) : (
														<span className="text-muted-foreground">—</span>
													)}
												</TableCell>
												<TableCell>
													{person.is_pep === true ? (
														<Badge variant="warning">Ano</Badge>
													) : person.is_pep === false ? (
														<Badge variant="secondary">Ne</Badge>
													) : (
														<span className="text-muted-foreground">—</span>
													)}
												</TableCell>
												<TableCell>
													<VerificationStatusBadge
														entries={person.client_links.map((link) => ({
															verified: link.verified ?? false,
															verification_expires_at:
																link.verification_expires_at,
														}))}
													/>
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</div>
				</div>
			</Dashboard>
		</>
	);
}

function ClientLinksBadge({
	clientLinks,
}: {
	clientLinks: PhysicalPersonsClientLink[];
}) {
	if (clientLinks.length === 0) {
		return <span className="text-muted-foreground">—</span>;
	}

	const sortedLinks = sortClientLinks(clientLinks);
	const primaryLink = sortedLinks[0];
	const additionalCount = sortedLinks.length - 1;

	const primaryText = getClientDisplayName(primaryLink);

	if (additionalCount > 0) {
		return (
			<Tooltip>
				<TooltipTrigger
					render={
						<Badge variant="outline" className="cursor-default text-xs">
							{primaryText} +{additionalCount}
						</Badge>
					}
				/>
				<TooltipContent>
					<ul className="space-y-1">
						{sortedLinks.map((link) => (
							<li key={link.client_id} className="flex items-center gap-2">
								<span>{getClientDisplayName(link)}</span>
								<VerificationDot verified={link.verified} />
							</li>
						))}
					</ul>
				</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<Badge variant="outline" className="text-xs">
			{primaryText}
		</Badge>
	);
}

function VerificationDot({ verified }: { verified?: boolean }) {
	if (verified) {
		return <span className="size-2 rounded-full bg-primary" />;
	}
	return <span className="size-2 rounded-full bg-muted-foreground/30" />;
}

function MissingInfoList({ missingInfo }: { missingInfo: string[] }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="font-medium">Chybějící údaje:</span>
			<ul className="list-inside list-disc">
				{missingInfo.map((info) => (
					<li key={info}>{MISSING_INFO_LABELS[info] || info}</li>
				))}
			</ul>
		</div>
	);
}
